import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Shield, Settings } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import AdminNotificationTest from "./AdminNotificationTest";
import FCMDebugPanel from "./FCMDebugPanel";

export const AdminPage: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState("notifications");

    // Aquí puedes implementar una lógica para verificar si el usuario es administrador
    // Por ejemplo, verificando su rol en Firebase o en tu backend
    const isAdmin = true; // Reemplaza esto con tu lógica real de verificación de administrador

    if (!isAdmin) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-4">
                    <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
                        {t("admin.accessDenied", "Acceso denegado")}
                    </h2>
                    <p className="mt-2 text-red-600 dark:text-red-400">
                        {t("admin.noPermission", "No tienes permisos para acceder a esta sección")}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex items-center mb-6">
                <Shield className="mr-2 text-gray-500 dark:text-gray-400" />
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {t("admin.title", "Panel de Administración")}
                </h1>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* Barra lateral con opciones */}
                <div className="md:col-span-3">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                            <Settings className="mr-2 h-5 w-5" />
                            {t("admin.tools", "Herramientas")}
                        </h2>
                        <ul className="space-y-2">
                            <li>
                                <button
                                    onClick={() => setActiveTab("notifications")}
                                    className={`w-full text-left px-3 py-2 rounded-md ${activeTab === "notifications"
                                            ? "bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400"
                                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        }`}
                                >
                                    {t("admin.notifications", "Notificaciones")}
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => setActiveTab("debug")}
                                    className={`w-full text-left px-3 py-2 rounded-md ${activeTab === "debug"
                                            ? "bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400"
                                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        }`}
                                >
                                    Diagnóstico FCM
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => setActiveTab("users")}
                                    className={`w-full text-left px-3 py-2 rounded-md ${activeTab === "users"
                                            ? "bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400"
                                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        }`}
                                >
                                    {t("admin.users", "Gestión de usuarios")}
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => setActiveTab("analytics")}
                                    className={`w-full text-left px-3 py-2 rounded-md ${activeTab === "analytics"
                                            ? "bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400"
                                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        }`}
                                >
                                    {t("admin.analytics", "Analíticas")}
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Contenido principal */}
                <div className="md:col-span-9">
                    {/* Mostrar componente según la pestaña activa */}
                    {activeTab === "notifications" && <AdminNotificationTest />}
                    {activeTab === "debug" && <FCMDebugPanel />}

                    {/* Sección para mostrar información del usuario actual */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {t("admin.currentUser", "Usuario actual")}
                        </h2>
                        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                            <p className="mb-2">
                                <span className="font-medium">{t("admin.userId", "ID de usuario")}:</span>{" "}
                                <span className="text-sm font-mono break-all">{user?.firebaseId}</span>
                            </p>
                            <p className="mb-2">
                                <span className="font-medium">{t("admin.email", "Correo electrónico")}:</span>{" "}
                                <span>{user?.email}</span>
                            </p>
                            <p>
                                <span className="font-medium">{t("admin.verified", "Verificado")}:</span>{" "}
                                <span>{user?.emailVerified ? "Sí" : "No"}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPage;